import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
// eslint-disable-next-line import/no-extraneous-dependencies
import { gsap } from "gsap";

import Button from "@components/button/button";
import AnimateCoins from "@components/coins/animate-coins";
import Coins from "@components/coins/coins";
import AnimateElemIndicatorSVG from "@components/icons/animate-elem-indicator-svg";
import CapitanGreatingSVG from "@components/icons/capitan-greating-svg";
import QueryHandler from "@components/query-handler/query-handler";
import ShareIcons from "@components/share/share-icons";

import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import { strokeDashoffset } from "@helpers/helpers";
import { ILesson } from "@typings/global.types";

import { useUpdateUserInfoMutation } from "../../api/user-info";

type LessonPropsType = {
  coins: number;
  lessonCoins: number;
  gameMode: boolean;
  lastLesson: ILesson;
  nextExerciseId: number;
  firstExerciseId: number;
};

function LessonComplete({
  coins,
  lessonCoins,
  gameMode,
  lastLesson,
  nextExerciseId,
  firstExerciseId,
}: LessonPropsType) {
  const navigate = useNavigate();

  const [btnNextActive, setBtnNextActive] = useState<boolean>(true);
  const [btnPrevActive, setBtnPrevActive] = useState<boolean>(false);
  const btnPrevRef = useRef<HTMLButtonElement>(null);
  const btnNextRef = useRef<HTMLButtonElement>(null);

  const [
    editUser,
    {
      isLoading: editUserIsLoading,
      isError: editUserIsError,
      error: editUserError,
      isSuccess: editUserIsSuccess,
    },
  ] = useUpdateUserInfoMutation();

  // Перемикаємо на наступний урок
  const handleNextLesson = () => {
    editUser({
      params: {
        currentExerciseId: nextExerciseId,
      },
    });
  };

  const handleRepeatLesson = () => {
    editUser({
      params: {
        currentExerciseId: firstExerciseId,
      },
    });
  };

  const onDonateClickHandler = () => {
    document.cookie = `eventCategory=Donate_link_lesson${lastLesson.orderId}; path=/;`;
  };
  const coverText = (word: any, lessonId: number) => (
    <a
      id={`Donate_link_lesson${lessonId}`}
      onClick={onDonateClickHandler}
      href={formatUrlWithLocale("/donation/")}
    >
      {word}
    </a>
  );

  useEffect(() => {
    const hotKey = (event: any): void => {
      if (event.keyCode === 39) {
        // next
        setBtnNextActive(true);
        setBtnPrevActive(false);
        btnNextRef.current?.focus();
      }
      if (event.keyCode === 37) {
        setBtnNextActive(false);
        setBtnPrevActive(true);
        btnPrevRef.current?.focus();
      }
      if (event.keyCode === 32 || event.keyCode === 13) {
        if (btnNextActive) {
          handleNextLesson();
        }
        if (btnPrevActive) {
          handleRepeatLesson();
        }
      }
    };
    window.addEventListener("keydown", hotKey);

    return () => window.removeEventListener("keydown", hotKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btnNextActive, btnPrevActive]);

  useEffect(() => {
    // закриваємо блок після вдалої зміни урока
    if (editUserIsSuccess) {
      navigate(formatUrlWithLocale("/typing-tutor/"));
    }
  }, [editUserIsSuccess, navigate]);

  const refs = [
    useRef<SVGSVGElement | null>(null),
    useRef<SVGSVGElement | null>(null),
    useRef<SVGSVGElement | null>(null),
  ];

  useEffect(() => {
    gsap.to(
      refs.map((ref) => ref.current),
      {
        opacity: 1,
        stagger: 0.2,
        duration: 1.2,
        y: 0,
        yoyo: true,
        ease: "bounce.out",
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countStars = strokeDashoffset(
    lastLesson.exercisesCount,
    lastLesson.exercisesWithStarCount,
  );

  const countColorStars =
    strokeDashoffset(
      lastLesson.exercisesCount,
      lastLesson.exercisesWithStarCount,
    ) === 150
      ? "stroke-gray"
      : "stroke-green";

  const countTargets = strokeDashoffset(
    lastLesson.exercisesCount,
    lastLesson.exercisesWithTargetCount,
  );

  const countColorTargets =
    strokeDashoffset(
      lastLesson.exercisesCount,
      lastLesson.exercisesWithTargetCount,
    ) === 150
      ? "stroke-gray"
      : "stroke-orange";

  const countLightnings = strokeDashoffset(
    lastLesson.exercisesCount,
    lastLesson.exercisesWithLightningCount,
  );

  const countColorLightnings =
    strokeDashoffset(
      lastLesson.exercisesCount,
      lastLesson.exercisesWithLightningCount,
    ) === 150
      ? "stroke-gray"
      : "stroke-purple";

  return (
    <QueryHandler
      isLoading={editUserIsLoading || (!editUserIsLoading && editUserIsSuccess)}
      isError={editUserIsError}
      errorMessage={editUserError}
    >
      <section className='mx-auto mb-6 max-w-container-xsmall text-center md:mb-8'>
        <CapitanGreatingSVG
          height={100}
          width={100}
          classNameProps='mx-auto my-0'
        />
        <h2>
          <FormattedMessage
            id='lesson-complete-title'
            values={{
              lessonNumber: lastLesson.orderId,
            }}
          />
        </h2>

        {gameMode ? (
          <div className='mb-4 flex items-center justify-center'>
            {lessonCoins > 0 ? (
              <AnimateCoins
                coins={coins - lessonCoins}
                currentCoins={lessonCoins}
              />
            ) : (
              <Coins coins={coins} />
            )}
          </div>
        ) : null}
        <div className='mb-5 flex justify-center'>
          <AnimateElemIndicatorSVG
            ref={refs[0]}
            d='M36.5416 23.0555L39.0657 29.3114C39.2936 29.8777 39.8321 30.2634 40.4461 30.3036L47.2491 30.7409C48.6903 30.8341 49.2782 32.6192 48.1678 33.5312L42.9237 37.8356C42.4491 38.2241 42.2444 38.8493 42.3968 39.4386L44.0762 45.9669C44.4332 47.3477 42.8961 48.4517 41.6754 47.6904L35.9102 44.0943C35.3892 43.7674 34.7244 43.7674 34.2048 44.0943L28.4396 47.6904C27.2174 48.4517 25.6818 47.3477 26.0374 45.9669L27.7182 39.4386C27.8691 38.8493 27.6659 38.2241 27.1913 37.8356L21.9472 33.5312C20.8368 32.6192 21.4232 30.8341 22.8645 30.7409L29.669 30.3036C30.2829 30.2634 30.8214 29.8777 31.0493 29.3114L33.5719 23.0555C34.1075 21.7292 36.0075 21.7292 36.5416 23.0555Z'
            colorPathCircle={countColorStars}
            classNameProps='sm:w-[70px] w-[70px] sm:h-[70px] h-[70px] opacity-0 -translate-y-8'
            strokeDashoffset={countStars}
          />
          <AnimateElemIndicatorSVG
            ref={refs[1]}
            d='M34.9992 31.2801C32.9484 31.2801 31.2785 32.9485 31.2785 35.0008C31.2785 37.0501 32.9484 38.72 34.9992 38.72C37.0515 38.72 38.7199 37.0501 38.7199 35.0008C38.7199 32.9485 37.0515 31.2801 34.9992 31.2801ZM34.9993 42.646C30.7839 42.646 27.3526 39.2162 27.3526 35.0008C27.3526 30.784 30.7839 27.3542 34.9993 27.3542C39.2146 27.3542 42.6474 30.784 42.6474 35.0008C42.6474 39.2162 39.2146 42.646 34.9993 42.646ZM34.9992 22.5555C28.1381 22.5555 22.5555 28.1381 22.5555 35.0007C22.5555 41.8619 28.1381 47.4444 34.9992 47.4444C41.8604 47.4444 47.4444 41.8619 47.4444 35.0007C47.4444 28.1381 41.8604 22.5555 34.9992 22.5555Z'
            colorPathCircle={countColorTargets}
            classNameProps='sm:w-[70px] w-[70px] sm:h-[70px] h-[70px] opacity-0 -translate-y-8'
            strokeDashoffset={countTargets}
          />
          <AnimateElemIndicatorSVG
            ref={refs[2]}
            d='M36.0403 21.5005L27.1372 31.3087C26.072 32.4859 26.4534 34.4599 27.8654 35.0718L33.2704 37.4111L30.8599 46.5323C30.4132 48.2191 32.3939 49.3827 33.4535 48.0582L42.5841 36.6413C43.5272 35.4612 43.1416 33.6285 41.8198 33.0091L36.1665 30.3646L38.5396 23.2024C39.0861 21.5486 37.1762 20.2467 36.0403 21.5005Z'
            colorPathCircle={countColorLightnings}
            classNameProps='sm:w-[70px] w-[70px] sm:h-[70px] h-[70px] opacity-0 -translate-y-8'
            strokeDashoffset={countLightnings}
          />
        </div>
        <div className='flex justify-center'>
          <Button
            onClick={handleRepeatLesson}
            className={classNames("btn-primary mr-4", {
              "outline outline-2 outline-offset-2 outline-state-focus-primary":
                btnPrevActive,
            })}
            ref={btnPrevRef}
          >
            <FormattedMessage id='repeat-lesson' />
          </Button>
          <Button
            onClick={handleNextLesson}
            className={classNames("btn-primary", {
              "outline outline-2 outline-offset-2 outline-state-focus-primary":
                btnNextActive,
            })}
            ref={btnNextRef}
          >
            <FormattedMessage id='next-lesson' />
          </Button>
        </div>
        <hr className='dark:border-brd-dark-primary mt-5 mb-4 border-t-2 border-brd-primary' />
        <div className='dark:text-txt-dark-secondary pt-1 text-txt-secondary md:-mb-4 md:flex md:flex-wrap md:items-center md:justify-center'>
          <div className='mb-4 mr-1'>
            <FormattedMessage id='to-share' />
          </div>
          <ShareIcons />
        </div>
      </section>
    </QueryHandler>
  );
}

export default LessonComplete;
